import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar,
  Grid,
  Divider,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PhotoCamera,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PrivacyTip as PrivacyIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Player } from "../types";
import { apiRequest } from "../config/api";
import { ImageUploadService } from "../services/imageUpload";
import ImageCropDialog from "../components/ImageCropDialog";

const EMAIL_TYPE_LABELS: Record<string, string> = {
  tournament_invite: "Tournament invitations",
  tournament_invite_accepted: "Tournament invite accepted",
  tournament_invite_declined: "Tournament invite declined",
  tournament_confirmation: "Tournament registration confirmation",
  tournament_waitlist: "Tournament waitlist updates",
  waitlist_promotion: "Waitlist promotion (a spot opened up)",
  tournament_reminder: "Tournament reminders",
  tournament_results: "Tournament results",
  league_day_confirmation: "League day registration confirmation",
  league_day_reminder: "League day reminder (day before)",
  league_day_results: "League day results",
  season_registration_confirmation: "Season registration confirmation",
  welcome: "Welcome email",
  payment_reminder: "Payment reminders",
  season_invitation: "Season invitations",
};

function PlayerProfile() {
  const { playerUser, playerToken, playerLogout } = useAuth();
  const navigate = useNavigate();
  const [player, setPlayer] = useState<Player | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [email, setEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  
  // Privacy settings state
  const [displayName, setDisplayName] = useState("");
  const [hideFromPublicRankings, setHideFromPublicRankings] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [emailOptOuts, setEmailOptOuts] = useState<string[]>([]);
  const [emailTypes, setEmailTypes] = useState<string[]>([]);
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPlayerProfile();
    fetchEmailPreferences();
  }, []);

  // Scroll to #email-preferences (linked from email footers) once the page has rendered
  useEffect(() => {
    if (loadingProfile || window.location.hash !== "#email-preferences") return;
    document.getElementById("email-preferences")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loadingProfile]);

  const fetchEmailPreferences = async () => {
    try {
      const response = await apiRequest("/api/player/email-preferences", {
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEmailOptOuts(data.optedOut || []);
        setEmailTypes(data.availableTypes || []);
      }
    } catch (err) {
      console.error("Error fetching email preferences:", err);
    }
  };

  const handleSaveEmailPreferences = async () => {
    setSavingEmailPrefs(true);
    setSuccess("");
    setError("");
    try {
      const response = await apiRequest("/api/player/email-preferences", {
        method: "PUT",
        headers: { Authorization: `Bearer ${playerToken}` },
        body: JSON.stringify({ optedOut: emailOptOuts }),
      });
      if (response.ok) {
        setSuccess("Email preferences saved successfully!");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to save email preferences");
      }
    } catch (err) {
      console.error("Error saving email preferences:", err);
      setError("Failed to save email preferences");
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const toggleEmailType = (type: string, receive: boolean) => {
    setEmailOptOuts((prev) => (receive ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const fetchPlayerProfile = async () => {
    try {
      const response = await apiRequest("/api/player/me", {
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPlayer(data);
        setEmail(data.email || "");
        setDisplayName(data.displayName || "");
        setHideFromPublicRankings(data.hideFromPublicRankings || false);
      }
    } catch (error) {
      console.error("Error fetching player profile:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleEmailUpdate = async () => {
    setError("");
    setSuccess("");

    if (!email) {
      setError("Email is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsUpdatingEmail(true);

    try {
      const response = await apiRequest("/api/player/me/email", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSuccess("Email updated successfully! You can now use password reset via email.");
        // Update local player state
        if (player) {
          setPlayer({ ...player, email });
        }
      } else {
        const data = await response.json();
        setError(data.error || "Failed to update email");
      }
    } catch (error) {
      console.error("Error updating email:", error);
      setError("Failed to update email");
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setError("All password fields are required");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("New passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiRequest("/api/player/me/password", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (response.ok) {
        setSuccess("Password updated successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");

        // Refresh profile to update requirePasswordChange flag
        fetchPlayerProfile();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to update password");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setError("Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfilePictureChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !player) return;

    setError("");
    setSuccess("");
    setSelectedFile(file);

    // Create a URL for the selected image to display in crop dialog
    const imageUrl = URL.createObjectURL(file);
    setSelectedImageUrl(imageUrl);
    setCropDialogOpen(true);

    // Reset the input value so the same file can be selected again
    event.target.value = "";
  };

  const handleCropComplete = async (croppedImageBlob: Blob) => {
    if (!player) return;

    setCropDialogOpen(false);
    setUploadingImage(true);

    try {
      // Convert blob to file
      const croppedFile = new File(
        [croppedImageBlob],
        selectedFile?.name || "profile.jpg",
        { type: "image/jpeg" },
      );

      // Upload cropped image to Firebase Storage
      const downloadURL = await ImageUploadService.uploadProfilePicture(
        croppedFile,
        player.id,
      );

      // Update player profile with new image URL
      const response = await apiRequest("/api/player/me/profile-picture", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          profilePicture: downloadURL,
        }),
      });

      if (response.ok) {
        setSuccess("Profile picture updated successfully!");
        // Update local player state
        setPlayer({ ...player, profilePicture: downloadURL });
      } else {
        const data = await response.json();
        setError(data.error || "Failed to update profile picture");
      }
    } catch (error: any) {
      console.error("Error updating profile picture:", error);
      setError(error.message || "Failed to upload profile picture");
    } finally {
      setUploadingImage(false);
      // Clean up the object URL
      if (selectedImageUrl) {
        URL.revokeObjectURL(selectedImageUrl);
      }
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    // Clean up the object URL
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
    }
  };

  const handleSavePrivacySettings = async () => {
    setSavingPrivacy(true);
    setSuccess("");
    setError("");

    try {
      const response = await apiRequest("/api/player/privacy-settings", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          displayName: displayName.trim() || null,
          hideFromPublicRankings,
        }),
      });

      if (response.ok) {
        setSuccess("Privacy settings saved successfully!");
        fetchPlayerProfile(); // Refresh data
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving privacy settings:", error);
      setError("Failed to save settings");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await apiRequest("/api/player/export-data", {
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Create a downloadable JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `my-spl-data-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess("Data exported successfully!");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError("Failed to export data");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setError("Failed to export data");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") {
      setError('Please type "DELETE" to confirm');
      return;
    }

    setDeleting(true);
    setError("");

    try {
      const response = await apiRequest("/api/player/request-deletion", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });

      if (response.ok) {
        alert(
          "Account deletion request submitted successfully. An admin will review your request. You will be logged out now.",
        );
        playerLogout();
        navigate("/");
      } else {
        const data = await response.json();
        setError(data.error || "Failed to submit deletion request");
      }
    } catch (error) {
      console.error("Error requesting account deletion:", error);
      setError("Failed to submit deletion request");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Redirect if not logged in
  if (!playerUser) {
    return <Navigate to="/player/login" replace />;
  }

  if (loadingProfile) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
        <AppBar position="static" elevation={2}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate("/player/dashboard")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              My Profile
            </Typography>
            <IconButton
              color="inherit"
              onClick={() => navigate("/")}
              sx={{
                mr: { xs: 0.5, sm: 1 },
                display: { xs: "inline-flex", sm: "none" },
              }}
            >
              <HomeIcon />
            </IconButton>
            <Button
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
              sx={{
                mr: 1,
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              Home
            </Button>
            <IconButton
              color="inherit"
              onClick={() => navigate("/player/dashboard")}
              sx={{
                display: { xs: "inline-flex", sm: "none" },
              }}
            >
              <DashboardIcon />
            </IconButton>
            <Button
              color="inherit"
              startIcon={<DashboardIcon />}
              onClick={() => navigate("/player/dashboard")}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              Dashboard
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md">
          <Box
            sx={{
              minHeight: "calc(100vh - 64px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate("/player/dashboard")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Profile
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => navigate("/")}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <HomeIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/")}
            sx={{
              mr: 1,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Home
          </Button>
          <IconButton
            color="inherit"
            onClick={() => navigate("/player/dashboard")}
            sx={{
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <DashboardIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            onClick={() => navigate("/player/dashboard")}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Dashboard
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          My Profile
        </Typography>

        {player?.requirePasswordChange && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You are using a temporary password. Please change your password
            below.
          </Alert>
        )}

        {!player?.email && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              📧 Add Your Email Address
            </Typography>
            <Typography variant="body2">
              Please provide your email address for password reset, season
              registration confirmations, and important league updates. Your email
              will be stored according to our privacy policy.
            </Typography>
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Profile Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Box sx={{ position: "relative", mr: 2 }}>
                    <Avatar
                      src={player?.profilePicture}
                      sx={{ width: 80, height: 80 }}
                    >
                      {player?.name.charAt(0)}
                    </Avatar>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="profile-picture-upload"
                      type="file"
                      onChange={handleProfilePictureChange}
                      disabled={uploadingImage}
                    />
                    <label htmlFor="profile-picture-upload">
                      <IconButton
                        component="span"
                        disabled={uploadingImage}
                        sx={{
                          position: "absolute",
                          bottom: -5,
                          right: -5,
                          backgroundColor: "primary.main",
                          color: "white",
                          width: 30,
                          height: 30,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        {uploadingImage ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PhotoCamera sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </label>
                  </Box>
                  <Box>
                    <Typography variant="h6">{player?.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {player?.division}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      @{player?.username}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    First Name
                  </Typography>
                  <Typography variant="body1">{player?.firstName}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Name
                  </Typography>
                  <Typography variant="body1">{player?.lastName}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isUpdatingEmail}
                    helperText="Used for password reset, season registration confirmations, and updates"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleEmailUpdate}
                    disabled={isUpdatingEmail || !email || email === player?.email}
                    sx={{ mt: 1 }}
                  >
                    {isUpdatingEmail ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Save Email"
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Stats Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏆 Lifetime Statistics
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 2 }}
                >
                  All-time stats across all seasons
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Points
                    </Typography>
                    <Typography variant="h6">
                      {player?.totalPoints || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Games Played
                    </Typography>
                    <Typography variant="h6">
                      {player?.gamesPlayed || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Games Won
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {player?.gamesWon || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Games Lost
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {player?.gamesLost || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Matches Won
                    </Typography>
                    <Typography variant="h6">
                      {player?.matchesWon || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Matches Lost
                    </Typography>
                    <Typography variant="h6">
                      {player?.matchesLost || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Change Password */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <form onSubmit={handlePasswordChange}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          edge="end"
                        >
                          {showCurrentPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  helperText="At least 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ mt: 3 }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </form>
            </Paper>
          </Grid>

          {/* Email Preferences */}
          <Grid item xs={12} id="email-preferences" sx={{ scrollMarginTop: 80 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Email Preferences</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Choose which emails you want to receive. Switch off the ones you don't
                want. Important account emails (e.g. password reset) are always sent.
              </Typography>
              {emailTypes.map((type) => (
                <Box key={type}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!emailOptOuts.includes(type)}
                        onChange={(e) => toggleEmailType(type, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={EMAIL_TYPE_LABELS[type] || type}
                  />
                </Box>
              ))}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveEmailPreferences}
                disabled={savingEmailPrefs}
                startIcon={savingEmailPrefs ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ mt: 2 }}
              >
                {savingEmailPrefs ? "Saving..." : "Save Email Preferences"}
              </Button>
            </Paper>
          </Grid>

          {/* Privacy Settings */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PrivacyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Privacy Settings</Typography>
              </Box>

              {/* Display Name */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Display Name
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Choose how your name appears on public leaderboards and rankings.
                    Leave blank to use your real name.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Display Name (Optional)"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={player?.name || "Your Real Name"}
                    helperText={`Public will see: "${displayName.trim() || player?.name || "Your Name"}"`}
                    sx={{ mb: 2 }}
                  />

                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Current name:</strong> {player?.name}
                      <br />
                      <strong>Will display as:</strong>{" "}
                      {displayName.trim() || player?.name}
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>

              {/* Visibility Settings */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Public Visibility
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Control whether your profile appears in public rankings and
                    leaderboards.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideFromPublicRankings}
                        onChange={(e) => setHideFromPublicRankings(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Hide my profile from public rankings"
                  />

                  {hideFromPublicRankings && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Your profile will be hidden from public leaderboards. Only
                      admins and league participants will see your match results.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Save Privacy Settings Button */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleSavePrivacySettings}
                disabled={savingPrivacy}
                startIcon={savingPrivacy ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ mb: 3 }}
              >
                {savingPrivacy ? "Saving..." : "Save Privacy Settings"}
              </Button>

              <Divider sx={{ my: 3 }} />

              {/* Data Rights */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Your Data Rights (GDPR)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Under GDPR, you have the right to access, export, and delete your
                    personal data.
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportData}
                      fullWidth
                    >
                      Export My Data (JSON)
                    </Button>

                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Privacy Policy Accepted:</strong>{" "}
                        {player?.privacyPolicyAcceptedAt
                          ? (() => {
                              const date = player.privacyPolicyAcceptedAt;
                              // Handle Firestore Timestamp object {_seconds, _nanoseconds} or {seconds, nanoseconds}
                              if (typeof date === 'object' && date !== null) {
                                const seconds = (date as any)._seconds || (date as any).seconds;
                                if (seconds) {
                                  return new Date(seconds * 1000).toLocaleDateString();
                                }
                              }
                              // Handle ISO string or number
                              return new Date(date).toLocaleDateString();
                            })()
                          : "Not yet accepted"}
                      </Typography>
                    </Alert>
                  </Box>
                </CardContent>
              </Card>

              {/* Account Deletion */}
              <Card sx={{ border: "2px solid", borderColor: "error.main" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    fontWeight="bold"
                    color="error"
                  >
                    Danger Zone
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Request permanent deletion of your account and all associated data
                    ("Right to be Forgotten").
                  </Typography>

                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Warning:</strong> This action cannot be undone. All your
                      match history, statistics, and personal data will be permanently
                      deleted after admin review.
                    </Typography>
                  </Alert>

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    fullWidth
                  >
                    Request Account Deletion
                  </Button>
                </CardContent>
              </Card>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          <Typography variant="h6" color="error" fontWeight="bold">
            Confirm Account Deletion
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete all your data including match history,
            statistics, and personal information.
          </Alert>

          <Typography variant="body2" paragraph>
            An admin will review your deletion request. Once approved, all your
            data will be permanently removed from our systems within 30 days.
          </Typography>

          <TextField
            fullWidth
            label='Type "DELETE" to confirm'
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            sx={{ mt: 2 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleteConfirmation !== "DELETE" || deleting}
            startIcon={deleting && <CircularProgress size={20} />}
          >
            {deleting ? "Submitting..." : "Confirm Deletion"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Crop Dialog */}
      <ImageCropDialog
        open={cropDialogOpen}
        imageUrl={selectedImageUrl}
        onClose={handleCropCancel}
        onCropComplete={handleCropComplete}
      />
    </Box>
  );
}

export default PlayerProfile;
